import { initReactI18next } from 'react-i18next';
import { load as loadCLDR, loadMessages as loadKendoMessages } from '@progress/kendo-react-intl';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { createLogger, loggerStyles } from '@ricoh/ria.client.core/plugins/app/logger';
import DateTime from '@ricoh/ria.client.core/plugins/moment';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import type { I18nLocale } from './_config.type';

import { i18nConfig } from './_config';

const logger = createLogger('i18n');

/**
 * Load resources.
 *
 * @param {I18nLocale} locale Locale.
 */
async function loadResources(locale: I18nLocale) {
  const { messages, kendo, cldr } = await i18nConfig.getResources(locale);

  await Promise.all([
    ...Object.entries(messages).map(([namespace, resources]) => Promise.resolve(i18next.addResourceBundle(locale, namespace, resources))),
    loadKendoMessages(kendo, locale),
    loadCLDR(...cldr),
  ]);
}

/**
 * Set locale.
 *
 * @param {I18nLocale} locale Locale.
 */
export async function setLocale(locale: I18nLocale) {
  await loadResources(locale);
  await Promise.all([i18next.changeLanguage(locale), DateTime.locale(locale)]);
}

/** Init I18n. */
export async function init() {
  try {
    logger.write('info', `${loggerStyles.requestText('Init')} i18n`);

    await i18next
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        ns: i18nConfig.namespaces,
        defaultNS: i18nConfig.defaultNamespace,
        fallbackNS: i18nConfig.defaultNamespace,
        supportedLngs: i18nConfig.locales,
        fallbackLng: i18nConfig.fallbackLocale,
        partialBundledLanguages: true,
        interpolation: {
          escapeValue: false,
        },
        detection: {
          order: ['localStorage', 'navigator'],
        },
      });

    if (!i18nConfig.locales.includes(i18next.language)) {
      throw new ClientError({
        module: 'plugin',
        message: 'Language not supported',
        cause: { locale: i18next.language },
      });
    }

    await Promise.all([
      (async () => {
        if (i18next.language === 'en') {
          return;
        }

        await loadResources('en');
      })(),
      setLocale(i18next.language),
    ]);

    logger.writeWithData('info', `${loggerStyles.responseText('Done')} use i18n`, i18next.options);
  } catch (exception) {
    logger.writeWithData('error', `${loggerStyles.errorText('Error')} setup i18n failed`, exception);
  }
}
